import { client } from '../sanity'

export interface Listing {
  _id: string
  _type: 'listing'
  productId: string
  channelCode: string
  listingId?: string
  overridePrice?: number
  overrideTitle?: string
  overrideDescription?: string
  status: 'draft' | 'published' | 'archived'
  listingUrl?: string
  _createdAt?: string
  _updatedAt?: string
}

/**
 * Listing list item type - matches actual Sanity schema with product data
 */
export interface ListingListItem {
  _id: string
  _type: 'listing'
  title: string
  price: number
  channel: string
  status: 'not_synced' | 'synced' | 'error' | 'pending'
  relatedProduct: {
    _ref: string
    _id: string
    title?: string
    rrp?: number
    isPrintOnDemand?: boolean
    media?: {
      hero?: {
        url?: string
      }
      galleryImages?: Array<{
        url?: string
      }>
    }
  }
  linkedAt?: string
  _createdAt?: string
  _updatedAt?: string
}

/**
 * Fetch all listings with product data for list display
 */
export const getAllListings = async (): Promise<ListingListItem[]> => {
  return client.fetch(
    `*[_type == "listing"] | order(_updatedAt desc) {
      _id,
      _type,
      title,
      price,
      channel,
      status,
      relatedProduct-> {
        _id,
        _ref,
        title,
        rrp,
        isPrintOnDemand,
        media {
          hero {
            "url": asset->url
          },
          galleryImages[] {
            "url": asset->url
          }
        }
      },
      linkedAt,
      _createdAt,
      _updatedAt
    }`
  )
}

/**
 * Fetch active (synced) listings
 */
export const getActiveListings = async (): Promise<ListingListItem[]> => {
  return client.fetch(
    `*[_type == "listing" && status == "synced"] | order(_updatedAt desc) {
      _id,
      _type,
      title,
      price,
      channel,
      status,
      relatedProduct-> {
        _id,
        _ref,
        title,
        rrp,
        isPrintOnDemand,
        media {
          hero {
            "url": asset->url
          },
          galleryImages[] {
            "url": asset->url
          }
        }
      },
      linkedAt,
      _createdAt,
      _updatedAt
    }`
  )
}

/**
 * Listing detail type - matches actual Sanity schema with all related data
 */
export interface ListingDetail {
  _id: string
  _type: 'listing'
  title: string
  price: number
  channel: string
  status: 'not_synced' | 'synced' | 'error' | 'pending'
  description?: any[] // Portable text array
  relatedProduct: {
    _ref: string
    _id: string
    title?: string
    sku?: string
    rrp?: number
    cost?: number
    isPrintOnDemand?: boolean
    description?: any[]
    media?: {
      hero?: {
        url?: string
      }
      galleryImages?: Array<{
        url?: string
      }>
    }
  }
  externalListingId?: string
  linkedAt?: string
  _createdAt?: string
  _updatedAt?: string
}

/**
 * Create a listing in Sanity
 */
export interface CreateListingInput {
  productId: string
  channel: string
  title: string
  price: number
  description?: any[] // Portable text array
  status?: 'not_synced' | 'synced' | 'error' | 'pending'
}

export const createListing = async (input: CreateListingInput): Promise<{ _id: string }> => {
  const {
    productId,
    channel,
    title,
    price,
    description = [],
    status = 'not_synced',
  } = input

  const doc = {
    _type: 'listing' as const,
    title,
    price,
    channel,
    status,
    description,
    relatedProduct: { _type: 'reference' as const, _ref: productId },
    linkedAt: new Date().toISOString(),
  }

  const created = await client.create(doc as any)
  return { _id: created._id }
}

/**
 * Fetch a single listing by ID with all detail fields
 */
export const getListingById = async (id: string): Promise<ListingDetail | null> => {
  return client.fetch(
    `*[_type == "listing" && _id == $id][0] {
      _id,
      _type,
      title,
      price,
      channel,
      status,
      description,
      relatedProduct-> {
        _id,
        _ref,
        title,
        sku,
        rrp,
        cost,
        isPrintOnDemand,
        description,
        media {
          hero {
            "url": asset->url
          },
          galleryImages[] {
            "url": asset->url
          }
        }
      },
      externalListingId,
      linkedAt,
      _createdAt,
      _updatedAt
    }`,
    { id }
  )
}

/**
 * Fetch listings for a specific product
 */
export const getListingsByProductId = async (productId: string): Promise<ListingListItem[]> => {
  return client.fetch(
    `*[_type == "listing" && relatedProduct._ref == $productId] | order(_updatedAt desc) {
      _id,
      _type,
      title,
      price,
      channel,
      status,
      relatedProduct-> {
        _id,
        _ref,
        title,
        rrp,
        isPrintOnDemand,
        media {
          hero {
            "url": asset->url
          },
          galleryImages[] {
            "url": asset->url
          }
        }
      },
      linkedAt,
      _createdAt,
      _updatedAt
    }`,
    { productId }
  )
}

/**
 * Update an existing listing
 */
export interface UpdateListingInput {
  title?: string
  price?: number
  status?: 'not_synced' | 'synced' | 'error' | 'pending'
  description?: any[]
}

export const updateListing = async (id: string, data: UpdateListingInput): Promise<{ _id: string }> => {
  const patch = client.patch(id).set({
    ...(data.title !== undefined ? { title: data.title } : {}),
    ...(data.price !== undefined ? { price: data.price } : {}),
    ...(data.status !== undefined ? { status: data.status } : {}),
    ...(data.description !== undefined ? { description: data.description } : {}),
    _updatedAt: new Date().toISOString(),
  })
  const res = await patch.commit()
  return { _id: res._id }
}

/**
 * Publish a listing: set status to 'synced' and update linkedAt
 */
export const publishListing = async (id: string): Promise<{ _id: string }> => {
  const res = await client
    .patch(id)
    .set({
      status: 'synced',
      linkedAt: new Date().toISOString(),
      lastSync: new Date().toISOString(),
      _updatedAt: new Date().toISOString(),
    })
    .commit()
  return { _id: res._id }
}

/**
 * Delete a listing
 */
export const deleteListing = async (id: string): Promise<void> => {
  try {
    await client.delete(id)
  } catch (error) {
    console.error('Failed to delete listing:', error)
    throw new Error(`Failed to delete listing: ${error instanceof Error ? error.message : 'Unknown error'}`)
  }
}
